// Dev-only primitives gallery at `/dev/gallery` (design spec 2026-10-19 §10 Phase 1): every
// @companyos/ui primitive in its states, in whichever theme is active (the switch is at the top),
// so the look can be reviewed without Storybook and `e2e/visual.spec.ts` can pin it. Registered
// by router.tsx only when `import.meta.env.DEV`, so production builds never contain it. Sample
// labels are illustrative copy, not data.
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarStack,
  AvatarTile,
  BANDS,
  Badge,
  BandBadge,
  Board,
  BoardCard,
  BoardColumn,
  BoardView,
  type BoardViewColumn,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ButtonGroup,
  ButtonGroupText,
  CalendarView,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  type ChatMessage,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
  Composer,
  CountPill,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  DueLabel,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyState,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FilterChip,
  FilterChips,
  GalleryView,
  GroupHeader,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Input,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
  Kbd,
  Label,
  ListRow,
  ListView,
  MentionChip,
  MessageList,
  MetaCounts,
  NativeSelect,
  NativeSelectOption,
  Notice,
  PageEditor,
  PageHeader,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Progress,
  ProgressLabel,
  ProgressValue,
  PropertyChip,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  SaveDock,
  SaveIndicator,
  ScoreMark,
  SearchField,
  SectionHeading,
  Segmented,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  SettingsAside,
  SettingsLayout,
  SettingsSection,
  SetupSteps,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Skeleton,
  Sparkline,
  Spinner,
  StatGrid,
  StatTile,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TaskCard,
  type TaskSummary,
  Textarea,
  ThemeSwitch,
  ThreadPanel,
  TimelineView,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  ToggleRow,
  Toolbar,
  ToolbarButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TopicList,
  TrendLineChart,
  toast,
  UploadZone,
  ViewTabs,
  Wizard,
} from "@companyos/ui";
import { createRoute } from "@tanstack/react-router";
import {
  ArrowUpDownIcon,
  BoldIcon,
  Building2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  CircleCheckIcon,
  CircleDotIcon,
  ClockIcon,
  Columns3Icon,
  DollarSignIcon,
  FileTextIcon,
  GaugeIcon,
  InboxIcon,
  ListFilterIcon,
  MessageSquareIcon,
  MoreHorizontalIcon,
  PaperclipIcon,
  PlusIcon,
  Rows3Icon,
  SearchIcon,
  SendIcon,
  SettingsIcon,
  SquareKanbanIcon,
  TargetIcon,
  Trash2Icon,
  UploadIcon,
  UsersIcon,
} from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { rootRoute } from "./__root";

export function createGalleryRoute() {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: "dev/gallery",
    component: Gallery,
  });
}

function Section({
  id,
  title,
  children,
  wide = false,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <section
      aria-labelledby={`g-${id}`}
      data-gallery-section={id}
      className={wide ? "min-w-0 lg:col-span-2" : "min-w-0"}
    >
      <h2
        id={`g-${id}`}
        className="mb-3 text-2xs font-semibold tracking-caps text-muted-foreground uppercase"
      >
        {title}
      </h2>
      <div className="rounded-lg border border-border bg-card p-5">{children}</div>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

const WEEKS = [
  { week: "W38", score: 64, target: 70 },
  { week: "W39", score: 71, target: 70 },
  { week: "W40", score: 68, target: 72 },
  { week: "W41", score: 77, target: 72 },
  { week: "W42", score: 82, target: 75 },
];

const COMPANIES = ["Acme Recruiting", "Beta Logistics", "Northwind Staffing"];

function Gallery() {
  return (
    <main id="main" className="min-h-svh bg-background text-foreground">
      <div className="mx-auto max-w-page px-8 pt-7 pb-12 max-md:px-4 max-md:pt-5">
        <header className="mb-7 flex items-start justify-between gap-6">
          <div className="max-w-prose">
            <p className="mb-2 text-[10px] font-bold tracking-eyebrow text-eyebrow uppercase">
              Design system · dev only
            </p>
            <h1 className="font-heading text-2xl font-strong tracking-title text-foreground max-md:text-xl">
              Primitives gallery
            </h1>
            <p className="mt-1.5 text-sm leading-[1.65] text-muted-foreground">
              Every @companyos/ui primitive in its resting, hover-able and selected states, on the
              2026-10-19 tokens. Switch the theme to review dark.
            </p>
          </div>
          <ThemeSwitch />
        </header>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-x-8 gap-y-7 lg:grid-cols-2">
          <Section id="buttons" title="Buttons" wide>
            <div className="flex flex-col gap-4">
              <Row>
                <Button size="lg">
                  <PlusIcon data-icon="inline-start" />
                  New report
                </Button>
                <Button>Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">
                  <Trash2Icon data-icon="inline-start" />
                  Reverse import
                </Button>
                <Button variant="link">Link</Button>
                <Button disabled>Disabled</Button>
              </Row>
              <Row>
                <Button size="sm">Small</Button>
                <Button size="sm" variant="secondary">
                  <UploadIcon data-icon="inline-start" />
                  Import CSV
                </Button>
                <Button size="sm" variant="ghost">
                  Ghost small
                </Button>
                <Button size="icon-sm" variant="ghost" aria-label="Settings">
                  <SettingsIcon />
                </Button>
                <Button size="icon" variant="secondary" aria-label="Search">
                  <SearchIcon />
                </Button>
                <Button size="icon-lg" aria-label="Add">
                  <PlusIcon />
                </Button>
                <ButtonGroup aria-label="Week">
                  <Button variant="secondary" size="icon" aria-label="Previous week">
                    <ChevronLeftIcon />
                  </Button>
                  <ButtonGroupText>W42 · 13–19 Oct</ButtonGroupText>
                  <Button variant="secondary" size="icon" aria-label="Next week">
                    <ChevronRightIcon />
                  </Button>
                </ButtonGroup>
                <Toggle aria-label="Bold" defaultPressed>
                  <BoldIcon />
                </Toggle>
                <Toggle variant="outline" aria-label="Bold, outline">
                  <BoldIcon />
                </Toggle>
              </Row>
            </div>
          </Section>

          <Section id="badges" title="Badges, bands and keys">
            <div className="flex flex-col gap-4">
              <Row>
                <Badge>Neutral</Badge>
                <Badge variant="accent">Accent</Badge>
                <Badge variant="success" dot>
                  Sent
                </Badge>
                <Badge variant="warning" dot>
                  Stale
                </Badge>
                <Badge variant="danger">Failed</Badge>
                <Badge variant="info">Generated</Badge>
                <Badge variant="outline">Outline</Badge>
              </Row>
              <Row>
                {BANDS.map((band) => (
                  <BandBadge key={band} band={band} />
                ))}
              </Row>
              <Row>
                <ScoreMark state="hit" />
                <ScoreMark state="miss" />
                <ScoreMark state="none" />
                <Sparkline label="Score, 5 weeks: 64 to 82" values={[64, 71, 68, 77, 82]} />
                <span className="text-sm text-muted-foreground">
                  Search <Kbd>⌘</Kbd>
                  <Kbd>K</Kbd>
                </span>
                <Spinner />
              </Row>
            </div>
          </Section>

          <Section id="avatars" title="Avatars, progress, skeleton">
            <div className="flex flex-col gap-4">
              <Row>
                <Avatar size="sm">
                  <AvatarFallback>MR</AvatarFallback>
                </Avatar>
                <Avatar>
                  <AvatarFallback>TK</AvatarFallback>
                </Avatar>
                <Avatar size="lg">
                  <AvatarFallback>AC</AvatarFallback>
                </Avatar>
                <AvatarGroup>
                  <Avatar size="sm">
                    <AvatarFallback>MR</AvatarFallback>
                  </Avatar>
                  <Avatar size="sm">
                    <AvatarFallback>TK</AvatarFallback>
                  </Avatar>
                  <AvatarGroupCount>+4</AvatarGroupCount>
                </AvatarGroup>
              </Row>
              <Progress value={60}>
                <ProgressLabel>Setup checklist</ProgressLabel>
                <ProgressValue />
              </Progress>
              <div className="flex items-center gap-3" aria-busy="true">
                <Skeleton className="size-9" />
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-3.5 w-1/2" />
                  <Skeleton className="h-3.5 w-1/3" />
                </div>
              </div>
            </div>
          </Section>

          <Section id="stats" title="Stat tiles" wide>
            <div className="grid grid-cols-4 gap-4 max-md:grid-cols-2 max-md:gap-3">
              <StatTile label="KPI score" value="82" band="great" detail="target 75 · +5 vs W41" />
              <StatTile label="Goal score" value="71" icon={TargetIcon} detail="3 of 4 goals hit" />
              <StatTile
                label="Cost this week"
                value="$1.2k"
                icon={DollarSignIcon}
                detail="client $940 · agency $260"
              />
              <StatTile
                label="Supervisor hours"
                value="6.5h"
                icon={ClockIcon}
                detail="Awaiting data for Fri"
              />
            </div>
          </Section>

          <Section id="fields" title="Fields">
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="g-name">Company name</FieldLabel>
                <Input id="g-name" defaultValue="Acme Recruiting" />
                <FieldDescription>Shown on the Friday report.</FieldDescription>
              </Field>
              <div className="grid grid-cols-2 gap-5">
                <Field>
                  <FieldLabel htmlFor="g-cur">Currency</FieldLabel>
                  <NativeSelect id="g-cur" defaultValue="usd" className="w-full">
                    <NativeSelectOption value="usd">USD</NativeSelectOption>
                    <NativeSelectOption value="eur">EUR</NativeSelectOption>
                  </NativeSelect>
                </Field>
                <Field>
                  <FieldLabel htmlFor="g-week">Week</FieldLabel>
                  <Select defaultValue="w42">
                    <SelectTrigger id="g-week" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="w41">W41</SelectItem>
                      <SelectItem value="w42">W42</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field data-invalid="true">
                <FieldLabel htmlFor="g-slug">Slug</FieldLabel>
                <Input id="g-slug" defaultValue="Acme!" aria-invalid="true" />
                <FieldError>Use lowercase letters, numbers and dashes.</FieldError>
              </Field>
              <Field>
                <FieldLabel htmlFor="g-note">Note for the client</FieldLabel>
                <Textarea id="g-note" placeholder="What moved this week…" />
              </Field>
              <Field>
                <FieldLabel htmlFor="g-company">Company</FieldLabel>
                <Combobox items={COMPANIES}>
                  <ComboboxInput id="g-company" placeholder="Pick a company" />
                  <ComboboxContent>
                    <ComboboxEmpty>No company found.</ComboboxEmpty>
                    <ComboboxList>
                      {(item: string) => (
                        <ComboboxItem key={item} value={item}>
                          {item}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </Field>
            </FieldGroup>
          </Section>

          <Section id="toggles" title="Toggles and choices">
            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between gap-5">
                <Label htmlFor="g-auto">Send the Friday report automatically</Label>
                <Switch id="g-auto" defaultChecked />
              </div>
              <div className="flex items-center justify-between gap-5 border-t border-subtle pt-4">
                <Label htmlFor="g-cc">Copy the agency on every report</Label>
                <Switch id="g-cc" />
              </div>
              <div className="flex items-center gap-2 border-t border-subtle pt-4">
                <Checkbox id="g-ack" defaultChecked />
                <Label htmlFor="g-ack">I have copied the key</Label>
                <Checkbox id="g-ack2" className="ml-4" />
                <Label htmlFor="g-ack2">Not yet</Label>
              </div>
              <ToggleGroup
                variant="chip"
                defaultValue={["sent"]}
                aria-label="Report state"
                className="border-t border-subtle pt-4"
              >
                <ToggleGroupItem value="all">All</ToggleGroupItem>
                <ToggleGroupItem value="sent">Sent</ToggleGroupItem>
                <ToggleGroupItem value="draft">Draft</ToggleGroupItem>
                <ToggleGroupItem value="stale">Stale</ToggleGroupItem>
              </ToggleGroup>
              <Collapsible defaultOpen className="border-t border-subtle pt-4">
                <CollapsibleTrigger render={<Button variant="ghost" size="sm" />}>
                  <ChevronsUpDownIcon data-icon="inline-start" />
                  Weeks
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 text-sm text-muted-foreground">
                  W42 · this week, W41, W40
                </CollapsibleContent>
              </Collapsible>
            </div>
          </Section>

          <Section id="tabs" title="Tabs">
            <div className="flex flex-col gap-6">
              <Tabs defaultValue="client">
                <TabsList aria-label="Cost ledger">
                  <TabsTrigger value="client">Client</TabsTrigger>
                  <TabsTrigger value="agency">Agency</TabsTrigger>
                  <TabsTrigger value="both">Both</TabsTrigger>
                </TabsList>
                <TabsContent value="client" className="text-muted-foreground">
                  Client rows
                </TabsContent>
              </Tabs>
              <Tabs defaultValue="scorecard">
                <TabsList variant="line" aria-label="DE profile">
                  <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
                  <TabsTrigger value="timeline">Timeline</TabsTrigger>
                  <TabsTrigger value="cost">Cost</TabsTrigger>
                  <TabsTrigger value="keys">Keys</TabsTrigger>
                </TabsList>
                <TabsContent value="scorecard" className="text-muted-foreground">
                  Scorecard body
                </TabsContent>
              </Tabs>
            </div>
          </Section>

          <Section id="nav" title="Breadcrumb and pagination">
            <div className="flex flex-col gap-5">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#scorecard">Scorecard</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#des">Digital employees</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Maya</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious href="#p1" />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href="#p1">1</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href="#p2" isActive>
                      2
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext href="#p3" />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </Section>

          <Section id="table" title="Table" wide>
            <Table>
              <TableCaption>Digital employees, week W42</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Seat</TableHead>
                  <TableHead>Band</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableHead scope="row">Maya</TableHead>
                  <TableCell>Recruiting coordinator</TableCell>
                  <TableCell>
                    <BandBadge band="great" />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">82</TableCell>
                  <TableCell className="text-right tabular-nums">38.5</TableCell>
                </TableRow>
                <TableRow data-state="selected">
                  <TableHead scope="row">Theo</TableHead>
                  <TableCell>Sourcer</TableCell>
                  <TableCell>
                    <BandBadge band="average" />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">61</TableCell>
                  <TableCell className="text-right tabular-nums">40.0</TableCell>
                </TableRow>
                <TableRow>
                  <TableHead scope="row">Iris</TableHead>
                  <TableCell>Scheduler</TableCell>
                  <TableCell>
                    <BandBadge band="insufficient_data" />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">—</TableCell>
                  <TableCell className="text-right tabular-nums">12.0</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Section>

          <Section id="cards" title="Card and items">
            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Friday report · W42</CardTitle>
                  <CardDescription>Generated Fri 17 Oct · 3 recipients</CardDescription>
                  <CardAction>
                    <Badge variant="info">Generated</Badge>
                  </CardAction>
                </CardHeader>
                <CardContent className="text-muted-foreground">
                  The note and the recipients sit above the preview.
                </CardContent>
                <CardFooter className="justify-end">
                  <Button variant="secondary" size="sm">
                    Open
                  </Button>
                  <Button size="sm">Send</Button>
                </CardFooter>
              </Card>
              <ItemGroup>
                <Item variant="outline" role="listitem">
                  <ItemMedia variant="icon">
                    <GaugeIcon />
                  </ItemMedia>
                  <ItemContent>
                    <ItemTitle>Maya</ItemTitle>
                    <ItemDescription>Recruiting coordinator · 38.5 h</ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <BandBadge band="great" />
                  </ItemActions>
                </Item>
              </ItemGroup>
            </div>
          </Section>

          <Section id="notices" title="Alerts and empty state">
            <div className="flex flex-col gap-4">
              <Alert>
                <InboxIcon />
                <AlertTitle>Demo accounts</AlertTitle>
                <AlertDescription>
                  Until Better Auth is wired, sign in as a demo user.
                </AlertDescription>
              </Alert>
              <Alert variant="destructive">
                <AlertTitle>The scorecard for W42 could not be loaded</AlertTitle>
                <AlertDescription>Try again in a minute.</AlertDescription>
              </Alert>
              <Empty className="border border-dashed border-border py-8">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <InboxIcon />
                  </EmptyMedia>
                  <EmptyTitle>Nothing here yet</EmptyTitle>
                  <EmptyDescription>The inbox arrives with chat and work (M2).</EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button size="sm" variant="secondary">
                    Set up a DE
                  </Button>
                </EmptyContent>
              </Empty>
            </div>
          </Section>

          <Section id="chart" title="Chart and resizable" wide>
            <div className="grid gap-6 lg:grid-cols-2">
              <TrendLineChart
                label="KPI score and target, W38 to W42"
                data={WEEKS}
                xKey="week"
                series={[{ key: "score", label: "KPI score" }]}
                referenceKey="target"
                className="h-48"
              />
              <ResizablePanelGroup
                orientation="horizontal"
                className="min-h-48 rounded-lg border border-border"
              >
                <ResizablePanel defaultSize="35%" className="bg-sidebar p-4 text-sm">
                  Sidebar
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel className="p-4 text-sm text-muted-foreground">Main</ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </Section>

          <Section id="floating" title="Floating surfaces" wide>
            <FloatingSurfaces />
          </Section>

          <Section id="command" title="Command palette (inline)">
            <Command className="rounded-lg border border-border">
              <CommandInput placeholder="Search or jump…" />
              <CommandList>
                <CommandGroup heading="Modes">
                  <CommandItem>
                    <GaugeIcon />
                    Scorecard
                    <CommandShortcut>G K</CommandShortcut>
                  </CommandItem>
                  <CommandItem>
                    <SettingsIcon />
                    Setup
                    <CommandShortcut>G ,</CommandShortcut>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </Section>

          <Section id="separator" title="Separator">
            <div className="flex flex-col gap-3 text-sm">
              <span>Above</span>
              <Separator />
              <div className="flex h-5 items-center gap-3">
                <span>Left</span>
                <Separator orientation="vertical" />
                <span>Right</span>
              </div>
            </div>
          </Section>
        </div>

        <Templates />
        <ChatAndWork />
      </div>
    </main>
  );
}

/**
 * Page templates (design spec §6, §8.2), each on the canvas the way a screen uses it rather than
 * boxed in a gallery card, so spacing and surfaces read as they will in the app.
 */
function Templates() {
  const [view, setView] = useState("grid");
  const [state, setState] = useState<"all" | "sent" | "draft">("all");
  const [ledger, setLedger] = useState<"client" | "agency">("client");
  const [sort, setSort] = useState<"ascending" | "descending">("descending");
  const [open, setOpen] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [doneCollapsed, setDoneCollapsed] = useState(true);
  return (
    <section aria-labelledby="g-templates" data-gallery-section="templates" className="mt-12">
      <h2
        id="g-templates"
        className="mb-6 border-t border-border pt-8 text-2xs font-semibold tracking-caps text-muted-foreground uppercase"
      >
        Page templates
      </h2>

      <div className="flex flex-col gap-10">
        <div data-gallery-template="header">
          <PageHeader
            breadcrumbs={
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#scorecard">Scorecard</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Maya</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            }
            eyebrow="Scorecard · W42"
            title="Acme Recruiting"
            badge={<Badge variant="success">On track</Badge>}
            help="The KPI score is the weighted share of targets hit this week; goals count once."
            subtitle="Week 13–19 Oct · all data in · 4 digital employees"
            actions={
              <>
                <Button size="lg">
                  <SendIcon data-icon="inline-start" />
                  Send report
                </Button>
                <Button size="lg" variant="secondary">
                  Enter hours
                </Button>
              </>
            }
            menuActions={[{ label: "Send report" }, { label: "Enter hours" }]}
            spacing="tabs"
          />
          <ViewTabs
            aria-label="Scorecard views"
            value={view}
            onValueChange={setView}
            tabs={[
              { value: "grid", label: "Weekly grid", icon: Rows3Icon },
              { value: "reports", label: "Reports", icon: FileTextIcon, count: 3 },
              { value: "board", label: "Board", icon: SquareKanbanIcon },
            ]}
          />
          <Toolbar>
            <Segmented
              aria-label="Report state"
              value={state}
              onValueChange={setState}
              options={[
                { value: "all", label: "All" },
                { value: "sent", label: "Sent" },
                { value: "draft", label: "Draft" },
              ]}
            />
            <SearchField className="max-sm:hidden" />
            <ToolbarButton
              icon={ListFilterIcon}
              label="Filter"
              active
              count={2}
              className="ml-auto"
            />
            <ToolbarButton icon={ArrowUpDownIcon} label="Sort" />
            <ToolbarButton icon={Rows3Icon} label="Group" />
            <ToolbarButton icon={Columns3Icon} label="Columns" />
            <Button size="sm">
              <PlusIcon data-icon="inline-start" />
              New
            </Button>
          </Toolbar>
          <FilterChips>
            <FilterChip label="State: sent" onRemove={() => {}} removeLabel="Remove State: sent" />
            <FilterChip label="DE: Maya" onRemove={() => {}} removeLabel="Remove DE: Maya" />
          </FilterChips>
        </div>

        <div data-gallery-template="stats">
          <SectionHeading
            title="Stat grid"
            description="StatGrid of StatTiles; the first is a link."
          />
          <StatGrid className="my-0">
            <StatTile label="KPI score" value="82" band="great" detail="target 75" href="#kpi" />
            <StatTile label="Goal score" value="71" icon={TargetIcon} detail="3 of 4 goals hit" />
            <StatTile
              label="Cost this week"
              value="$1.2k"
              icon={DollarSignIcon}
              detail="client $940"
            />
            <StatTile
              label="Supervisor hours"
              value="6.5h"
              icon={ClockIcon}
              detail="of 8 planned"
            />
          </StatGrid>
        </div>

        <div className="grid gap-x-8 gap-y-10 lg:grid-cols-2">
          <div data-gallery-template="list">
            <SectionHeading
              title="List rows"
              count={4}
              actions={
                <Segmented
                  size="sm"
                  aria-label="Ledger"
                  value={ledger}
                  onValueChange={setLedger}
                  options={[
                    { value: "client", label: "Client" },
                    { value: "agency", label: "Agency" },
                  ]}
                />
              }
            />
            <div className="overflow-hidden rounded-lg border border-border">
              <GroupHeader label="This week" count={2} open={open} onOpenChange={setOpen} />
              {open ? (
                <>
                  <ListRow
                    lead={<CircleDotIcon />}
                    title="Friday report · W42"
                    href="#w42"
                    chips={<Badge variant="info">Generated</Badge>}
                    meta="Fri 17 Oct"
                    trailing={
                      <AvatarStack
                        items={[
                          { name: "Maya Ross" },
                          { name: "Theo King" },
                          { name: "Iris Lund" },
                          { name: "Ana Diaz" },
                        ]}
                      />
                    }
                    actions={
                      <Button size="icon-xs" variant="ghost" aria-label="Report actions">
                        <MoreHorizontalIcon />
                      </Button>
                    }
                    selected
                  />
                  <ListRow
                    lead={<CircleDotIcon />}
                    title="Hours import · 42 rows"
                    href="#import"
                    chips={
                      <Badge variant="warning" dot>
                        Stale
                      </Badge>
                    }
                    meta="Thu 16 Oct"
                    trailing={<AvatarTile name="Maya Ross" size="xs" />}
                  />
                </>
              ) : null}
              <GroupHeader label="Earlier" count={2} />
              <ListRow
                lead={<CircleCheckIcon />}
                title="Friday report · W41"
                href="#w41"
                chips={<Badge variant="success">Sent</Badge>}
                meta="Fri 10 Oct"
                trailing={<AvatarTile name="Recruit bot" kind="agent" size="xs" />}
                done
              />
              <ListRow
                lead={<CircleCheckIcon />}
                title="Friday report · W40"
                href="#w40"
                meta="Fri 3 Oct"
                trailing={
                  <MetaCounts items={[{ icon: MessageSquareIcon, count: 3, label: "comments" }]} />
                }
                done
              />
            </div>
          </div>

          <div data-gallery-template="table">
            <SectionHeading
              title="Table"
              description="Sortable header, numeric cells, empty body."
            />
            <Table>
              <TableCaption side="top">Digital employees, week W42</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead
                    numeric
                    sort={sort}
                    onSort={() => setSort((s) => (s === "ascending" ? "descending" : "ascending"))}
                  >
                    Score
                  </TableHead>
                  <TableHead numeric onSort={() => {}}>
                    Hours
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableHead scope="row">Maya</TableHead>
                  <TableCell numeric>82</TableCell>
                  <TableCell numeric>38.5</TableCell>
                </TableRow>
                <TableRow data-state="selected">
                  <TableHead scope="row">Theo</TableHead>
                  <TableCell numeric>61</TableCell>
                  <TableCell numeric>40.0</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Table>
              <TableBody>
                <TableEmpty
                  colSpan={3}
                  icon={InboxIcon}
                  title="No imports yet"
                  description="CSV batches you commit show up here."
                />
              </TableBody>
            </Table>
          </div>
        </div>

        <div data-gallery-template="board">
          <SectionHeading title="Board" description="Columns 300 wide; a collapsed column is 40." />
          <Board>
            <BoardColumn
              title="To do"
              count={2}
              actions={
                <Button size="icon-sm" variant="ghost" aria-label="Add to To do">
                  <PlusIcon />
                </Button>
              }
            >
              <BoardCard
                title="Review the W42 numbers before the Friday report goes out"
                onSelect={() => {}}
                chips={[
                  <Badge key="kpi" variant="accent">
                    KPI
                  </Badge>,
                  <Badge key="w42">W42</Badge>,
                  <Badge key="m">Maya</Badge>,
                  <Badge key="t">Theo</Badge>,
                ]}
                avatars={<AvatarStack items={[{ name: "Maya Ross" }, { name: "Theo King" }]} />}
                due="Fri"
                meta={
                  <MetaCounts
                    items={[
                      { icon: MessageSquareIcon, count: 2, label: "comments" },
                      { icon: PaperclipIcon, count: 1, label: "files" },
                    ]}
                  />
                }
              />
              <BoardCard title="Import last week's hours" onSelect={() => {}} due="Mon" ghost />
            </BoardColumn>
            <BoardColumn title="In progress" count={1}>
              <BoardCard
                title="Set targets for the sourcing seat"
                onSelect={() => {}}
                chips={[<Badge key="s">Seats</Badge>]}
                avatars={<AvatarTile name="Iris Lund" size="xs" />}
                selected
              />
            </BoardColumn>
            <BoardColumn
              title="Done"
              count={8}
              collapsed={doneCollapsed}
              onCollapsedChange={setDoneCollapsed}
            >
              <BoardCard title="Invite the agency" onSelect={() => {}} dragging />
            </BoardColumn>
          </Board>
        </div>

        <div data-gallery-template="settings">
          <SettingsLayout
            aside={
              <SettingsAside title="About these settings">
                The company name and currency appear on every Friday report. Changes apply from the
                next report.
              </SettingsAside>
            }
          >
            <SettingsSection
              id="g-company"
              icon={Building2Icon}
              title="Company"
              description="Name, currency and the reporting calendar."
            >
              <div className="grid grid-cols-2 gap-5 max-md:grid-cols-1">
                <Field>
                  <FieldLabel htmlFor="g-t-name">Company name</FieldLabel>
                  <Input id="g-t-name" defaultValue="Acme Recruiting" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="g-t-cur">Currency</FieldLabel>
                  <NativeSelect id="g-t-cur" defaultValue="usd" className="w-full">
                    <NativeSelectOption value="usd">USD</NativeSelectOption>
                    <NativeSelectOption value="eur">EUR</NativeSelectOption>
                  </NativeSelect>
                </Field>
              </div>
              <div>
                <ToggleRow
                  label="Send the Friday report automatically"
                  description="Every Friday at 16:00 to the client recipients."
                  defaultChecked
                />
                <ToggleRow label="Copy the agency on every report" />
              </div>
            </SettingsSection>
            <div className="flex items-center justify-end gap-3">
              <SaveIndicator savedAt={1} />
              <SaveDock
                dirty
                onSave={() => {}}
                onDiscard={() => {}}
                className="static max-md:static"
              />
            </div>
          </SettingsLayout>
        </div>

        <div data-gallery-template="wizard">
          <SectionHeading title="Wizard" description="Import CSV, step 2 of 3." />
          <Wizard
            aria-label="Import steps"
            current={1}
            steps={[
              { label: "Upload", hint: "A CSV of hours or numbers" },
              { label: "Review", hint: "Dry run, row by row" },
              { label: "Commit", hint: "Write the batch" },
            ]}
            footer={
              <>
                <Button size="lg" variant="secondary">
                  Back
                </Button>
                <Button size="lg">Continue</Button>
              </>
            }
          >
            <SettingsSection title="Review the dry run" icon={UploadIcon}>
              <UploadZone
                title="Drop a CSV here or browse"
                hint="Up to 5 MB · .csv"
                accept=".csv"
              />
            </SettingsSection>
          </Wizard>
          <div className="mt-6">
            <SetupSteps
              aria-label="Setup checklist"
              steps={[
                { label: "Invite people", hint: "2 people", state: "done", href: "#people" },
                { label: "Add a seat", hint: "1 seat", state: "done", href: "#seats" },
                {
                  label: "Set metrics",
                  hint: "Targets per seat",
                  state: "current",
                  href: "#metrics",
                },
                {
                  label: "Put a DE on a seat",
                  hint: "From Setup › DEs",
                  state: "todo",
                  href: "#des",
                },
                { label: "Enter hours", hint: "This week", state: "todo", href: "#hours" },
              ]}
            />
          </div>
        </div>

        <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2" data-gallery-template="states">
          <div className="flex flex-col gap-3">
            <Notice tone="neutral">The SHA of this report is 4f2a…e91.</Notice>
            <Notice tone="info" title="Demo accounts">
              Until Better Auth is wired, sign in as a demo user.
            </Notice>
            <Notice tone="success" dismissible>
              The W42 report was sent to 3 recipients.
            </Notice>
            <Notice
              tone="warning"
              action={
                <Button size="sm" variant="secondary">
                  Open add-ons
                </Button>
              }
            >
              KPI is read-only until 31 Oct.
            </Notice>
            <Notice tone="danger" role="alert" title="The scorecard for W42 could not be loaded">
              Try again in a minute.
            </Notice>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              Counts <CountPill count={3} /> <CountPill count={12} tone="accent" />
              <CountPill count={140} tone="warning" />
              <AvatarTile name="Maya Ross" size="lg" />
              <AvatarTile name="Theo King" size="lg" />
              <AvatarTile name="Iris Lund" size="lg" />
              <AvatarTile name="Recruit bot" kind="agent" size="lg" />
              <AvatarTile name="Acme" kind="company" size="lg" />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <EmptyState
              variant="card"
              icon={UsersIcon}
              title="No digital employees yet"
              description="Set up a DE, put it on a seat, and its scorecard starts with the next week."
              actions={<Button>Set up a DE</Button>}
            />
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={() => setDrawer(true)}>
                Open drawer
              </Button>
              <span className="text-sm text-muted-foreground">Detail drawer (Escape closes)</span>
            </div>
          </div>
        </div>
      </div>

      <Drawer open={drawer} onOpenChange={setDrawer}>
        <DrawerHeader
          actions={
            <Button size="sm" variant="secondary">
              Open profile
            </Button>
          }
        >
          Scorecard › Digital employees
        </DrawerHeader>
        <DrawerBody>
          <DrawerTitle>Maya</DrawerTitle>
          <div className="mt-3 -mx-2 flex flex-wrap gap-1">
            <PropertyChip>
              <CircleDotIcon />
              Active
            </PropertyChip>
            <PropertyChip>
              <TargetIcon />
              Recruiting coordinator
            </PropertyChip>
          </div>
          <DrawerSection title="This week" className="mt-6">
            <p className="text-sm text-muted-foreground">KPI score 82 · 38.5 h · on track.</p>
          </DrawerSection>
        </DrawerBody>
      </Drawer>
    </section>
  );
}

/**
 * Menus, popovers and tooltips are shown open (non-modal) so the screenshot pins them; dialogs,
 * the alert dialog, the sheet and toasts open from their buttons.
 */
// Chat, editor and work views (UI-01). The clock is fixed so day captions and relative times
// render the same in every screenshot; the people, tasks and messages are illustrative copy.
const NOW = new Date(2026, 9, 19, 10, 0);
const at = (day: number, h: number, m: number) => new Date(2026, 9, day, h, m).toISOString();
const MAYA = { id: "u-maya", name: "Maya Ross" };
const THEO = { id: "u-theo", name: "Theo King" };
const SCOUT = { id: "a-scout", name: "Scout", kind: "agent" as const };

const MESSAGES: ChatMessage[] = [
  {
    id: "m1",
    author: MAYA,
    createdAt: at(16, 16, 40),
    body: "The W41 scorecard is in. Placements are up, but the screening backlog grew again.",
    reactions: [{ emoji: "👀", count: 2, names: ["Theo King", "Scout"] }],
  },
  {
    id: "m2",
    author: { id: "sys", name: "System" },
    system: true,
    createdAt: at(16, 16, 52),
    body: "Theo renamed the topic to “W42 backlog”.",
  },
  {
    id: "m3",
    author: SCOUT,
    createdAt: at(19, 9, 4),
    body: (
      <>
        Overnight I screened 38 of the 52 waiting candidates. 9 are ready for{" "}
        <MentionChip name="Theo King" />.
      </>
    ),
    thread: {
      replyCount: 3,
      lastReplyAt: at(19, 9, 40),
      participants: [THEO, MAYA],
      unread: true,
    },
    embed: (
      <TaskCard
        variant="inline"
        onOpen={() => {}}
        task={{
          id: "t-screen",
          title: "Screen the remaining 14 candidates",
          location: "Recruiting › Pipeline",
          status: { label: "In progress", tone: "accent", kind: "active" },
          assignees: [{ name: "Scout", kind: "agent" }],
          due: "2026-10-20",
          priority: "high",
          counts: { subtasks: { done: 2, total: 5 }, comments: 4 },
        }}
      />
    ),
  },
  {
    id: "m4",
    author: THEO,
    createdAt: at(19, 9, 31),
    body: "Thanks. I'll take the first five this morning.",
    reactions: [
      { emoji: "👍", count: 2, reacted: true, names: ["Maya Ross", "you"] },
      { emoji: "✅", count: 1, names: ["Scout"] },
    ],
  },
  {
    id: "m5",
    author: THEO,
    createdAt: at(19, 9, 33),
    body: "And I attached the intake notes from Friday.",
    attachments: [
      { id: "f1", name: "intake-notes-w42.pdf", description: "PDF · 240 KB", href: "#" },
      { id: "f2", name: "shortlist.csv", description: "Uploading…", state: "uploading" },
    ],
  },
  {
    id: "m6",
    author: MAYA,
    createdAt: at(19, 9, 52),
    body: "Great. Let's review the shortlist at 11.",
    pinned: true,
  },
];

const TOPICS = [
  {
    id: "w42",
    name: "W42 backlog",
    lastActivityAt: at(19, 9, 52),
    unreadCount: 3,
    preview: "Maya: Great. Let's review the shortlist at 11.",
    participants: [MAYA, THEO, SCOUT],
  },
  {
    id: "hiring",
    name: "Hiring a second sourcer",
    lastActivityAt: at(18, 15, 10),
    preview: "Theo: the job post is live on two boards",
  },
  {
    id: "export",
    name: "Weekly export failed",
    lastActivityAt: at(12, 8, 0),
    preview: "Scout: fixed, the feed token had expired",
    resolved: true,
  },
];

const TASKS: TaskSummary[] = [
  {
    id: "t1",
    title: "Review the W42 numbers before the Friday report goes out",
    status: { label: "To do", tone: "neutral", kind: "todo" },
    labels: [{ label: "KPI", tone: "accent" }],
    assignees: [{ name: "Maya Ross" }],
    due: "2026-10-23",
    priority: "medium",
    counts: { comments: 2 },
  },
  {
    id: "t2",
    title: "Screen the remaining 14 candidates",
    status: { label: "In progress", tone: "accent", kind: "active" },
    assignees: [{ name: "Scout", kind: "agent" }, { name: "Theo King" }],
    start: "2026-10-17",
    due: "2026-10-20",
    priority: "high",
    counts: { subtasks: { done: 2, total: 5 }, comments: 4, files: 1 },
  },
  {
    id: "t3",
    title: "Renew the job board subscription",
    status: { label: "Blocked", tone: "warning", kind: "blocked" },
    assignees: [{ name: "Theo King" }],
    due: "2026-10-16",
    overdue: true,
    priority: "urgent",
  },
  {
    id: "t4",
    title: "Send the W41 report to the client",
    status: { label: "Done", tone: "success", kind: "done" },
    done: true,
    assignees: [{ name: "Maya Ross" }],
    start: "2026-10-12",
    due: "2026-10-16",
    priority: "low",
  },
];

const COLUMNS: BoardViewColumn[] = [
  { id: "todo", title: "To do", tone: "neutral", tasks: [TASKS[0] as TaskSummary] },
  { id: "doing", title: "In progress", tone: "accent", tasks: [TASKS[1] as TaskSummary] },
  { id: "blocked", title: "Blocked", tone: "warning", tasks: [TASKS[2] as TaskSummary] },
  { id: "done", title: "Done", tone: "success", tasks: [TASKS[3] as TaskSummary] },
];

function moveTask(cols: BoardViewColumn[], id: string, to: string, index: number) {
  const task = cols.flatMap((c) => c.tasks).find((t) => t.id === id);
  if (!task) return cols;
  return cols.map((c) => {
    const rest = c.tasks.filter((t) => t.id !== id);
    if (c.id !== to) return { ...c, tasks: rest };
    return { ...c, tasks: [...rest.slice(0, index), task, ...rest.slice(index)] };
  });
}

function ChatAndWork() {
  const [topic, setTopic] = useState("w42");
  const [topicName, setTopicName] = useState("W42 backlog");
  const [columns, setColumns] = useState(COLUMNS);
  const [selected, setSelected] = useState<string[]>(["t2"]);
  const [mode, setMode] = useState<"month" | "week">("month");
  const [anchor, setAnchor] = useState("2026-10-19");
  const [following, setFollowing] = useState(true);
  const mentionables = [
    { id: MAYA.id, name: MAYA.name, hint: "Recruiting lead" },
    { id: THEO.id, name: THEO.name, hint: "Sourcer" },
    { id: SCOUT.id, name: SCOUT.name, kind: "agent" as const },
  ];
  const commands = [
    { id: "task", title: "Task", description: "Make this a task", onRun: () => {} },
  ];
  return (
    <section aria-labelledby="g-chat-work" data-gallery-section="chat-work" className="mt-12">
      <h2
        id="g-chat-work"
        className="mb-6 text-2xs font-semibold tracking-caps text-muted-foreground uppercase"
      >
        Chat, editor and work views
      </h2>
      <div className="flex flex-col gap-10">
        <div data-gallery-template="chat">
          <SectionHeading
            title="Channel"
            description="Topics, the message list, the composer and a thread in the side panel."
          />
          <div className="grid h-[640px] grid-cols-[minmax(0,1fr)] overflow-hidden rounded-lg border border-border bg-card lg:grid-cols-[260px_minmax(0,1fr)_360px] max-lg:h-auto">
            <TopicList
              topics={TOPICS}
              selectedId={topic}
              onSelect={setTopic}
              onNewTopic={() => {}}
              now={NOW}
              className="border-border-subtle border-r max-lg:border-r-0 max-lg:border-b"
            />
            <div className="flex min-h-0 flex-col max-lg:h-[560px]">
              <MessageList
                messages={MESSAGES}
                currentUserId={THEO.id}
                lastReadId="m4"
                now={NOW}
                onReact={() => {}}
                onOpenThread={() => {}}
                onPinChange={() => {}}
                onEdit={() => {}}
                onDelete={() => {}}
                hasEarlier
                onLoadEarlier={() => {}}
              />
              <div className="border-border-subtle border-t p-3">
                <Composer
                  placeholder="Message #recruiting"
                  topic={{
                    value: topicName,
                    onChange: setTopicName,
                    suggestions: TOPICS.map((t) => t.name),
                  }}
                  mentionables={mentionables}
                  commands={commands}
                  onAttach={() => {}}
                  onSubmit={() => {}}
                  attachments={
                    <AttachmentGroup>
                      <Attachment size="xs">
                        <AttachmentMedia>
                          <PaperclipIcon />
                        </AttachmentMedia>
                        <AttachmentContent>
                          <AttachmentTitle>offer-letter.docx</AttachmentTitle>
                          <AttachmentDescription>18 KB</AttachmentDescription>
                        </AttachmentContent>
                      </Attachment>
                    </AttachmentGroup>
                  }
                />
              </div>
            </div>
            <ThreadPanel
              className="border-border border-l max-lg:h-[520px] max-lg:border-t max-lg:border-l-0"
              root={MESSAGES[2] as ChatMessage}
              replies={[
                {
                  id: "r1",
                  author: THEO,
                  createdAt: at(19, 9, 20),
                  body: "Which five are the strongest?",
                },
                {
                  id: "r2",
                  author: SCOUT,
                  createdAt: at(19, 9, 21),
                  body: "Ranked by fit: Ana, Bo, Chen, Dee and Eli. Notes are on each card.",
                },
                {
                  id: "r3",
                  author: MAYA,
                  createdAt: at(19, 9, 40),
                  body: "Start with Chen, the client asked about her twice.",
                },
              ]}
              context="#recruiting › W42 backlog"
              following={following}
              onFollowingChange={setFollowing}
              onClose={() => {}}
              now={NOW}
              onReact={() => {}}
              composer={<Composer placeholder="Reply…" hint={false} onSubmit={() => {}} />}
            />
          </div>
        </div>

        <div data-gallery-template="page-editor">
          <SectionHeading
            title="Page editor"
            description="BlockNote (MPL core): / for blocks and commands, @ for people and agents."
          />
          <div className="rounded-lg border border-border bg-card px-8 py-7 max-md:px-4">
            <PageEditor
              title="Screening SOP"
              onTitleChange={() => {}}
              mentionables={mentionables}
              commands={commands}
              initialMarkdown={
                "## Before the call\n\nRead the intake notes and the client's must-haves.\n\n- Confirm availability\n- Confirm the salary band\n\n## After the call\n\nScore the candidate and move the card."
              }
            />
          </div>
        </div>

        <div data-gallery-template="board-view">
          <SectionHeading
            title="Board view"
            description="Drag cards, or focus one and press Alt with the arrow keys."
          />
          <BoardView
            columns={columns}
            selectedTaskId="t2"
            onTaskOpen={() => {}}
            onTaskMove={(id, to, index) => setColumns((c) => moveTask(c, id, to, index))}
            onAddTask={() => {}}
          />
        </div>

        <div data-gallery-template="list-view">
          <SectionHeading title="List view" description="Grouped by status; one row selected." />
          <ListView
            groups={columns.map((c) => ({ id: c.id, title: c.title, tasks: c.tasks }))}
            selectedIds={selected}
            onSelectionChange={setSelected}
            onTaskOpen={() => {}}
            onToggleDone={() => {}}
            onAddTask={() => {}}
          />
        </div>

        <div data-gallery-template="table-view">
          <SectionHeading title="Table view" description="TanStack Table; sorted by due date." />
          <TableView
            label="Tasks"
            rows={TASKS}
            getRowId={(t) => t.id}
            onRowOpen={() => {}}
            selectedRowId="t2"
            defaultSort={{ id: "due", desc: false }}
            columns={[
              { id: "title", header: "Task", accessor: (t) => t.title, sortable: true },
              {
                id: "status",
                header: "Status",
                accessor: (t) => t.status?.label,
                cell: (t) =>
                  t.status ? (
                    <Badge variant={t.status.tone ?? "neutral"}>{t.status.label}</Badge>
                  ) : null,
                sortable: true,
              },
              {
                id: "assignee",
                header: "Assignee",
                accessor: (t) => t.assignees?.[0]?.name,
              },
              {
                id: "due",
                header: "Due",
                accessor: (t) => t.due,
                cell: (t) => (t.due ? <DueLabel due={t.due} overdue={t.overdue} /> : null),
                sortable: true,
              },
              {
                id: "comments",
                header: "Comments",
                accessor: (t) => t.counts?.comments ?? 0,
                numeric: true,
                sortable: true,
              },
            ]}
          />
        </div>

        <div data-gallery-template="calendar-view">
          <SectionHeading
            title="Calendar view"
            description="By due date; drag a chip to reschedule."
          />
          <CalendarView
            anchor={anchor}
            today="2026-10-19"
            mode={mode}
            onModeChange={setMode}
            onNavigate={setAnchor}
            onItemOpen={() => {}}
            onItemMove={() => {}}
            onDayAdd={() => {}}
            items={[
              { id: "c1", title: "Friday report", date: "2026-10-23", tone: "accent" },
              { id: "c2", title: "Screen candidates", date: "2026-10-20", tone: "accent" },
              { id: "c3", title: "Renew job board", date: "2026-10-16", tone: "warning" },
              {
                id: "c4",
                title: "W41 report sent",
                date: "2026-10-16",
                tone: "success",
                done: true,
              },
              { id: "c5", title: "Client check-in", date: "2026-10-16", tone: "info" },
              { id: "c6", title: "Payroll export", date: "2026-10-16" },
              { id: "c7", title: "Shortlist review", date: "2026-10-19", tone: "accent" },
            ]}
          />
        </div>

        <div data-gallery-template="timeline-view">
          <SectionHeading
            title="Timeline view"
            description="Start → due bars; a diamond is due only."
          />
          <TimelineView
            from="2026-10-12"
            to="2026-10-25"
            today="2026-10-19"
            onItemOpen={() => {}}
            items={[
              {
                id: "l1",
                title: "Send the W41 report",
                start: "2026-10-12",
                end: "2026-10-16",
                done: true,
              },
              {
                id: "l2",
                title: "Screen the remaining candidates",
                start: "2026-10-17",
                end: "2026-10-20",
              },
              { id: "l3", title: "Renew the job board", end: "2026-10-16", tone: "warning" },
              {
                id: "l4",
                title: "Friday report W42",
                start: "2026-10-21",
                end: "2026-10-23",
                tone: "info",
              },
            ]}
          />
        </div>

        <div data-gallery-template="gallery-view">
          <SectionHeading title="Gallery view" description="Pages and files as cards." />
          <GalleryView
            selectedId="g2"
            onOpen={() => {}}
            items={[
              {
                id: "g1",
                title: "Screening SOP",
                description: "How we run a first call, from intake to score.",
                badges: [{ label: "SOP", tone: "accent" }],
                meta: "Edited 2h ago",
              },
              {
                id: "g2",
                title: "Client must-haves: Acme",
                description: "Shared with the client team.",
                icon: UsersIcon,
                badges: [{ label: "Policy", tone: "info" }],
                meta: "Edited yesterday",
              },
              {
                id: "g3",
                title: "Offer letter template",
                icon: FileTextIcon,
                meta: "Edited 12 Oct",
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function FloatingSurfaces() {
  return (
    // The open popups sit first with empty room above them: off-screen in a full-page capture,
    // Base UI flips them upwards, so that room keeps them clear of the buttons.
    <div className="flex flex-col-reverse gap-6">
      <Row>
        <Dialog>
          <DialogTrigger render={<Button variant="secondary" />}>Open dialog</DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New company</DialogTitle>
              <DialogDescription>Name it; the slug follows.</DialogDescription>
            </DialogHeader>
            <Field>
              <FieldLabel htmlFor="g-dlg-name">Name</FieldLabel>
              <Input id="g-dlg-name" />
            </Field>
            <DialogFooter>
              <Button variant="secondary">Cancel</Button>
              <Button size="lg">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <AlertDialog>
          <AlertDialogTrigger render={<Button variant="secondary" />}>
            Open alert dialog
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reverse this import?</AlertDialogTitle>
              <AlertDialogDescription>
                The 42 rows from this batch are removed from the scorecard.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction variant="destructive">Reverse</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Sheet>
          <SheetTrigger render={<Button variant="secondary" />}>Open sheet</SheetTrigger>
          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>Inbox</SheetTitle>
              <SheetDescription>Approvals, mentions and assignments.</SheetDescription>
            </SheetHeader>
          </SheetContent>
        </Sheet>
        <Button
          variant="secondary"
          onClick={() =>
            toast.add({ title: "Report sent", description: "3 recipients", type: "success" })
          }
        >
          Show toast
        </Button>
      </Row>
      <div className="grid grid-cols-3 gap-6 pt-48 max-md:grid-cols-1">
        <div>
          <DropdownMenu open modal={false}>
            <DropdownMenuTrigger render={<Button variant="secondary" />}>
              Account
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuGroup>
                <DropdownMenuLabel>Maya Ross</DropdownMenuLabel>
                <DropdownMenuItem>
                  Profile
                  <DropdownMenuShortcut>⌘P</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuCheckboxItem checked>Presenting</DropdownMenuCheckboxItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive">Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div>
          <Popover open>
            <PopoverTrigger render={<Button variant="secondary" />}>Scope</PopoverTrigger>
            <PopoverContent align="start">
              <PopoverHeader>
                <PopoverTitle>Scope</PopoverTitle>
                <PopoverDescription>Company, a team or one DE.</PopoverDescription>
              </PopoverHeader>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-start gap-6">
          <Tooltip open>
            <TooltipTrigger render={<Button variant="ghost" size="icon" aria-label="Inbox" />}>
              <InboxIcon />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Inbox <Kbd>G</Kbd>
              <Kbd>I</Kbd>
            </TooltipContent>
          </Tooltip>
          <HoverCard>
            <HoverCardTrigger
              href="#maya"
              className="text-sm text-primary underline-offset-4 hover:underline"
            >
              Maya
            </HoverCardTrigger>
            <HoverCardContent>Recruiting coordinator · W42 score 82</HoverCardContent>
          </HoverCard>
        </div>
      </div>
    </div>
  );
}
